<template>
  <div class="table-wrapper">
    <div class="section-heading">
      {{ $t('account.index.currencySettings.invoiceSettingTable.title') }}
    </div>
    <div class="section-sub-heading">
      {{ $t('account.index.currencySettings.invoiceSettingTable.subtitle') }}
    </div>
    <div class="table-inner-container drip-table-container">
      <div class="table-display" @scroll="onScrollDisplay">
        <b-table
          sort-icon-left
          :fields="$t('invoiceSettingsTableFields')"
          :items="invoices"
          :no-sort-reset="true"
          class="drip-v2-table table-borderless"
          tbody-tr-class="drip-tr-class"
          thead-class="drip-table-head"
        >
          <template #cell(reference)="data">
            <div
              class="truncate_text"
              style="max-width: 96px"
              :title="data.item.reference"
            >
              <router-link
                v-if="isDraft(data.item.stage_human)"
                :to="{ name: 'invoicesAdd', params: { id: data.item.id } }"
                v-track-click
                :id="`invoices-index-edit-ref-${data.item.reference}`"
              >
                {{ data.item.reference }}
              </router-link>
              <router-link
                v-else-if="isDocsPending(data.item.stage_human)"
                :to="{
                  name: 'invoicesAdd',
                  params: { id: data.item.id, value: 'upload-docs' }
                }"
                v-track-click
                :id="`invoices-index-edit-ref-${data.item.reference}`"
              >
                {{ data.item.reference }}
              </router-link>
              <router-link
                v-else-if="isReviewPending(data.item.stage_human)"
                :to="{
                  name: 'invoicesAdd',
                  params: { id: data.item.id, value: 'review' }
                }"
                v-track-click
                :id="`invoices-index-edit-ref-${data.item.reference}`"
              >
                {{ data.item.reference }}
              </router-link>
              <router-link
                v-else
                :to="{ name: 'invoicesSingle', params: { id: data.item.id } }"
                v-track-click
                :id="`invoices-index-view-ref-${data.item.reference}`"
              >
                {{ data.item.reference }}
              </router-link>
            </div>
          </template>
          <template #cell(importer)="data">
            <div class="truncate_text" style="max-width: 120px">
              {{ data.item.importer }}
            </div>
          </template>
          <template #cell(value)="data">
            {{ data.value | currency(data.item.currsym, null, 0) }}
          </template>
          <template #cell(drip_switch_enabled)="data">
            <div class="currency-status">
              <div class="title alternate">
                {{
                  $t(
                    'account.index.currencySettings.invoiceSettingTable.inrPayments'
                  )
                }}
                {{ data.item.drip_switch_enabled ? 'Enabled' : 'Disabled' }}
              </div>
              <div
                class="checkbox-container d-flex align-items-center justify-content-flex-end"
              >
                <toggle-switch
                  :value="data.item.drip_switch_enabled"
                  :onToggle="() => onSwitchChange(data.item)"
                  :tableCompatible="true"
                />
              </div>
            </div>
          </template>
          <template #cell(total_advanced)="data">
            <!-- We have 3 cases
          1. When We have advance.length === 0  -- show '-' (handled in v-else)
          2. When We have advance.length === 1 -- Show the transaction_amount
          3. When We have advance.length and drip_switch is enabled > 1 -- render redirection link to transactions page -->
            <template v-if="data.item.advances.length === 1">
              {{
                data.item.advances
                  .map((item) => parseFloat(item.value))
                  .reduce((prev, next) => prev + next)
                  | currency(data.item.currsym, null, 0)
              }}
              <div v-if="data.item.transaction_amount">
                {{
                  $t(
                    'account.index.currencySettings.invoiceSettingTable.advAmount.prepend'
                  )
                }}
                <b>₹{{ commaSepratedValue(data.item.transaction_amount) }}</b>
                {{
                  $t(
                    'account.index.currencySettings.invoiceSettingTable.advAmount.at'
                  )
                }}₹{{ data.item.rate }} per {{ data.item.base_currency }}
              </div>
            </template>
            <template
              v-else-if="
                data.item.drip_switch_enabled && data.item.advances.length > 1
              "
            >
              <div>
                {{
                  data.item.advances
                    .map((item) => parseFloat(item.value))
                    .reduce((prev, next) => prev + next)
                    | currency(data.item.currsym, null, 0)
                }}
              </div>
              <router-link :to="{ name: 'transactions' }" v-track-click>
                {{
                  $t(
                    'account.index.currencySettings.invoiceSettingTable.multipleAdvs'
                  )
                }}
              </router-link>
            </template>
            <template v-else> - </template>
          </template>
          <template #cell(stage_human)="data">
            <div
              class="invoice-status"
              :class="data.item.stage_human | invoiceStageClass"
            >
              <div
                class="invoices-stage-chip"
                :class="getInvoiceStageCategory(data.item.stage)"
              >
                {{
                  $t(`invoiceTableItems.status['${data.item.stage_human}']`)
                    | invoiceStageDisplay
                }}
              </div>
            </div>
          </template>
        </b-table>
        <loader :height="60" v-if="hasPages" />
      </div>
    </div>
    <enable-drip-switch-modal
      :refId="selectedInvoice.reference"
      :invoiceId="selectedInvoice.id"
      :invoiceLevel="true"
      :isEnableDripSwitchOpen="isEnableDripSwitchModalOpen"
      :dataToSend="getEnableDripSwitchApiData"
      :onClose="onModalClose"
      :onCancel="onModalCancel"
      :onSuccess="onSuccessEnabledSwitch"
    />
    <disable-drip-switch-sidebar
      :isDisableDripSwitchOpen="isDisableDripSwitchDrawerOpen"
      :dataToSend="getDisableDripSwitchApiData"
      :onCancel="onDrawerCancel"
      :onClose="onDrawerClose"
      :onSuccess="onSuccessDisableSwitch"
    />
    <disabled-message-modal
      :userLevel="false"
      :showDialog="showDisabledMessageModal"
      @closeDialog="onCloseDMMDialog"
    />
    <enabled-message-modal
      :userLevel="false"
      :showDialog="showEnabledMessageModal"
      @closeDialog="onCloseEMMDialog"
    />
  </div>
</template>
<script>
import invoiceService from '@/services/invoice'
import downloadService from '@/services/download/'
import ToggleSwitch from '@/components/common/Switch'
import EnableDripSwitchModal from '@/components/drip-switch/EnableDripSwitchModal'
import DisableDripSwitchSidebar from '@/components/drip-switch/DisableDripSwitchSidebar'
import Loader from '@/components/common/Loader'
import DisabledMessageModal from '@/components/drip-switch/DisabledMessageModal'
import EnabledMessageModal from '@/components/drip-switch/EnabledMessageModal'

export default {
  props: ['invoices', 'invoicesMeta', 'pagination', 'currentPage', 'loading'],
  components: {
    ToggleSwitch,
    EnableDripSwitchModal,
    DisableDripSwitchSidebar,
    Loader,
    DisabledMessageModal,
    EnabledMessageModal
  },
  data() {
    return {
      isEnableDripSwitchModalOpen: false,
      isDisableDripSwitchDrawerOpen: false,
      selectedInvoice: {},
      showDisabledMessageModal: false,
      showEnabledMessageModal: false
    }
  },
  methods: {
    onModalClose() {
      this.isEnableDripSwitchModalOpen = false
    },
    onModalCancel() {
      this.isEnableDripSwitchModalOpen = false
    },
    onDrawerClose() {
      this.isDisableDripSwitchDrawerOpen = false
    },
    onDrawerCancel() {
      this.isDisableDripSwitchDrawerOpen = false
    },
    onSuccessDisableSwitch() {
      this.$emit('refreshInvoices')
      this.showDisabledMessageModal = true
    },
    onSuccessEnabledSwitch() {
      this.$emit('refreshInvoices')
      this.showEnabledMessageModal = true
    },
    onCloseEMMDialog() {
      this.showEnabledMessageModal = false
    },
    onCloseDMMDialog() {
      this.showDisabledMessageModal = false
    },
    onDisableInrRemittance() {
      this.$mpTrack('Disable INR Remittances Clicked')
      this.isDisableDripSwitchDrawerOpen = true
    },
    onSwitchChange(item) {
      this.$mpTrack('Invoice Toggle Clicked', {
        currentValue: item.drip_switch_enabled,
        invoiceId: item.id
      })
      if (item.drip_switch_enabled) {
        this.selectedInvoice = item
        this.isDisableDripSwitchDrawerOpen = true
      } else {
        this.selectedInvoice = item
        this.isEnableDripSwitchModalOpen = true
      }
    },
    getInvoiceStageCategory(invoiceStage) {
      if (!this.invoiceMeta) {
        return ''
      }
      let stage = ''
      Object.keys(this.invoiceMeta.groups).forEach((category) => {
        if (this.invoiceMeta.groups[category].indexOf(invoiceStage) > -1) {
          stage = category
        }
      })
      return stage
    },
    onScrollDisplay({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollTop + clientHeight >= scrollHeight) {
        if (this.hasPages) {
          this.$emit('scrollBottom')
        }
      }
    },
    downloadInvoice(invoiceId) {
      return invoiceService
        .downloadInvoice(invoiceId)
        .then((res) => {
          downloadService.downloadPDF(
            res.data,
            `invoices-${invoiceId}-advice.pdf`
          )
        })
        .catch((e) => {
          console.log(e)
        })
    },
    isDraft(stage) {
      return [
        this.$t(`invoiceTableItems.status.Draft`),
        this.$t(`invoiceTableItems.status['Basic Info Pending']`)
      ].includes(stage)
    },
    isDocsPending(stage) {
      return stage === this.$t(`invoiceTableItems.status['Docs Pending']`)
    },
    isReviewPending(stage) {
      return stage === this.$store.state.constants.invoiceStage.review
    }
  },
  computed: {
    getDisableDripSwitchApiData() {
      return {
        invoice_id: this.selectedInvoice.id,
        user_id: this.$store.state.account.accountId,
        enabled: false
      }
    },
    getEnableDripSwitchApiData() {
      return {
        invoice_id: this.selectedInvoice.id,
        user_id: this.$store.state.account.accountId,
        enabled: true
      }
    },
    benefitsText() {
      return this.$t('account.index.currencySettings.benefits')
    },
    hasPages() {
      return this.currentPage < this.pagination.total_pages
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../../../assets/scss/variables';
@import '../../../assets/scss/palette';
.disableRemittance {
  max-width: 230px;
  margin-bottom: 30px;
}
.table-wrapper {
  margin-top: 64px;
  .section-heading {
    font-weight: 700;
    font-size: 24px;
    line-height: 19px;
    color: map-get($noble-blue, 500);
    margin-bottom: 1rem;
  }
  .section-sub-heading {
    font-size: 400;
    color: map-get($noble-blue, 500);
    margin-bottom: 16px;
  }
  .benefit-block {
    padding: 8px;
    padding-left: 16px;
    padding-right: 16px;
    display: flex;
    align-items: center;
    color: map-get($noble-blue, 500);
    font-weight: bold;
  }
  .table-inner-container {
    height: 800px;
  }
  .divider {
    border-right: 1px solid map-get($noble-blue, 200);
  }
}
@include media-breakpoint-down(md) {
  .benifit-container {
    flex-direction: column;
    align-items: center;
  }
  .benefit-block {
    margin-top: 10px;
    width: 100%;
  }
}
</style>
